import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getCsrfToken } from '@/lib/csrf';
import { logError } from '@/lib/logger';
import type { EmoviDocSseEvent } from '../types';

type SendMessageParams = {
  patientId: number;
  conversationId: number;
  message: string;
  onDone?: (messageId: number | null) => void;
};

export function useEmoviDocStream() {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [streamingReasoning, setStreamingReasoning] = useState('');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isStreamingRef = useRef(false);

  const resetStream = useCallback(() => {
    setStreamingText('');
    setStreamingReasoning('');
    setError(null);
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    isStreamingRef.current = false;
    setIsStreaming(false);
  }, []);

  useEffect(() => {
    return () => abort();
  }, [abort]);

  const sendMessage = useCallback(
    async ({ patientId, conversationId, message, onDone }: SendMessageParams) => {
      if (isStreamingRef.current) return;

      isStreamingRef.current = true;
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;
      const url = `/therapists/patients/${patientId}/emovi_doc/${conversationId}/stream`;

      try {
        await fetchEventSource(url, {
          method: 'POST',
          openWhenHidden: true,
          credentials: 'include',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
          body: JSON.stringify({ message }),
          onmessage(ev) {
            if (!ev.data) return;

            let payload: EmoviDocSseEvent | null = null;
            try {
              payload = JSON.parse(ev.data) as EmoviDocSseEvent;
            } catch {
              return;
            }

            if (!payload) return;

            if (payload.type === 'text') {
              setStreamingText((prev) => prev + payload.delta);
              return;
            }

            if (payload.type === 'reasoning') {
              setStreamingReasoning((prev) => prev + payload.delta);
              return;
            }

            if (payload.type === 'error') {
              setError(payload.message || 'Si è verificato un errore. Riprova tra qualche istante.');
              controller.abort();
              return;
            }

            if (payload.type === 'done') {
              onDone?.(payload.message_id ?? null);
              controller.abort();
            }
          },
          onerror(err) {
            if (err instanceof Error && err.name === 'FatalError') throw err;
            if (controller.signal.aborted) throw err;
          },
        });
      } catch (err) {
        if ((err as Error)?.name !== 'AbortError') {
          setError('Connessione interrotta. Verifica la tua connessione e riprova.');
          logError('emovidoc.stream.error', err);
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        isStreamingRef.current = false;
        setIsStreaming(false);
      }
    },
    [],
  );

  return {
    isStreaming,
    streamingText,
    streamingReasoning,
    error,
    sendMessage,
    resetStream,
    abort,
  };
}
